import type { ReactNode } from 'react';
import { Panel } from './Panel';

type EmptyStateProps = { title: string; body?: ReactNode; action?: ReactNode; className?: string };

/** Nothing to show, said plainly, with the one thing that would change that. One pattern everywhere (L-10). */
export function EmptyState({ title, body, action, className = '' }: EmptyStateProps) {
  return (
    <Panel padding="lg" className={`text-center ${className}`}>
      <h2 className="t-heading text-ink">{title}</h2>
      {body && <p className="mx-auto mt-2 max-w-md text-[14px] text-inkMuted">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </Panel>
  );
}

/** A route whose page lands in a later task. The app builds and navigates; the page says so. */
export function Placeholder({ name }: { name: string }) {
  return (
    <EmptyState
      title={`${name} is on its way.`}
      body="This screen is being built. The rest of the academy works around it."
    />
  );
}

/** While a query is in flight: quiet bars with a text equivalent. */
export function Loading({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`flex flex-col gap-3 ${className}`} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="h-3 rounded bg-rule" style={{ width: `${88 - i * 14}%` }} />
      ))}
    </div>
  );
}
